/**
 * SE-1883 Rotations page — the T13 destination for the `person_group`
 * axis. This page lists a program's rotations, creates / renames /
 * archives them, and per-rotation edits the directors AND the members
 * of each rotation (see `RotationDetailDialog.tsx`).
 *
 * TWO PLANES, ONE PAGE:
 *   - MEMBERSHIP is roster data, effective IMMEDIATELY, grants nothing.
 *     Wired to `PATCH /api/person-groups/{id}/members` (NAMED add/remove
 *     delta — never the declarative PUT; SE-1819 archived 30 sync-owned
 *     members via absence-as-verb).
 *   - DIRECTORSHIP is a grant, effective at NEXT SIGN-IN, and lives on
 *     the T4 grant plane. Wired to `PUT /api/user-access` with
 *     `person_group_scopes.<Role>` (declarative, ADMIN-only outbound —
 *     composed by `nextScopeSet` from the director's own fresh read;
 *     SE-1819 D3, SE-2841).
 *
 * Conflating their copy is the documented failure mode on this surface
 * (`domain/person-group` inv. 3a). All disclosure and success text lives
 * in `rotationCopy.ts`; a test asserts the membership file's tokens
 * never appear on a directorship surface and vice versa.
 *
 * ARCHIVE ORDERING (load-bearing): revoke the ADMIN director grants
 * FIRST — while the group is still an offered scope choice — THEN
 * PATCH `archived=true`. Once archived, `scope-choices` drops the group
 * and the binding can no longer be addressed. See `archiveRotation()`.
 *
 * NAME COLLISIONS: an ACTIVE label conflict returns 409 from the
 * service; the SPA surfaces that inline. An ARCHIVED label does NOT
 * block creation — a re-used name creates a brand new rotation with its
 * own identity, per the ticket's "no prompt" decision.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import { useTranslation } from "react-i18next";

import { ProgramRole } from "../../API";
import { useAppSelector } from "../../app/hooks";
import { selectAdminAuthority, selectUserRoles } from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import { administersProgram } from "../Auth/adminAccess";
import { PageWrapper } from "../../common-ui/PageWrapper";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import {
  alphabetizePrograms,
  programLabel,
  programsVisibleTo,
  useAllPrograms,
} from "../Programs/programUtils";
import {
  selectNetworkFeaturesByProgramId,
  selectNetworkFeaturesState,
} from "../Networks/networkFeaturesSlice";
import { fetchUsersByProgramIds } from "../Users/usersAPI";
import { RotationNetworkSelect } from "./RotationNetworkSelect";

import {
  useKnownRoles,
  usePersonGroups,
  useRoleScopes,
} from "./hooks";
import { RotationDetailDialog } from "./RotationDetailDialog";
import {
  Director,
  PersonGroup,
  RoleScope,
  ScopeBinding,
  createPersonGroup,
  fetchPersonGroupMembers,
  fetchUserAccess,
  groupByDirector,
  isAdminOwned,
  apiErrorMessage,
  nextScopeSet,
  patchPersonGroup,
  putUserAccess,
  rolesOnAxis,
} from "./simplAuthAPI";
import type { ProgramNetwork } from "./simplAuthAPI";
import { archiveCopy, refusalCopy, rotationAccessCopy } from "./rotationCopy";

// -----------------------------------------------------------------------------
// Types & helpers reused from the previous director-grant page.
// -----------------------------------------------------------------------------

type SaveKind = "assign" | "revoke";

type Grantee = {
  id: string;
  email: string;
  displayName: string;
};

/**
 * Director-picker candidate set: active Attendings and Rotation Directors
 * in the selected program who aren't also Trainees there. Deduplicates by
 * email and shapes into a Grantee tuple ready for the picker.
 *
 * SE-1765: directors are Attendings, or people invited as a Rotation
 * Director. Designating an Attending makes `PUT /user-access` give them
 * the RotationDirector programmembership in that program, so nobody has
 * to add the role through Edit User first. The Lambda refuses anyone
 * else.
 */
const directorGrantees = async (programId: string): Promise<Grantee[]> => {
  if (!programId) return [];
  const [attendings, directorRows, trainees] = await Promise.all([
    fetchUsersByProgramIds([programId], ProgramRole.Attending, true),
    fetchUsersByProgramIds([programId], ProgramRole.RotationDirector, true),
    fetchUsersByProgramIds([programId], ProgramRole.Trainee, true),
  ]);
  const rows = [...(attendings ?? []), ...(directorRows ?? [])];
  const traineeIds = new Set((trainees ?? []).map((row) => row.id));
  const seen = new Set<string>();
  const out: Grantee[] = [];
  for (const row of rows) {
    const email = (row.email ?? "").trim().toLowerCase();
    if (!email || seen.has(email) || traineeIds.has(row.id)) continue;
    seen.add(email);
    const fullName = [row.firstName, row.lastName]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(" ");
    out.push({
      id: row.id,
      email,
      displayName: fullName || email,
    });
  }
  out.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return out;
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const RotationsPage = () => {
  const { t } = useTranslation();

  // --- Programs the current caller can administer ---
  const { programs: administeredPrograms } =
    getProgramsCurrentUserCanAdminister();
  const {
    programs: allPrograms,
    status: programsStatus,
    error: programsError,
    getAllPrograms,
  } = useAllPrograms();

  const retryLoadPrograms = () => {
    void getAllPrograms();
  };

  const currentUsersRoles = useAppSelector(selectUserRoles);
  const isSysAdmin = !!currentUsersRoles?.includes(Role.SystemAdmin);
  // SE-1883: filter the picker to programs whose network has the
  // rotations feature. Same shape for SysAdmin and ProgramAdmin —
  // see network-features Lambda's SysAdmin branch.
  const featuresByProgramId = useAppSelector(selectNetworkFeaturesByProgramId);
  const { status: featuresStatus } = useAppSelector(selectNetworkFeaturesState);

  const adminAuthority = useAppSelector(selectAdminAuthority);

  // The token carries only the rotation ids a director directs; each
  // rotation's program comes from its detail read.
  const directedRotationKey = (adminAuthority?.directedRotationIds ?? []).join(",");
  // null while the director's rotations are still being read, so the page
  // waits instead of briefly saying they direct nothing (SE-2305).
  const [directedProgramIds, setDirectedProgramIds] = useState<
    string[] | null
  >(null);
  useEffect(() => {
    const ids = directedRotationKey ? directedRotationKey.split(",") : [];
    if (ids.length === 0) {
      setDirectedProgramIds([]);
      return;
    }
    setDirectedProgramIds(null);
    let cancelled = false;
    void Promise.allSettled(ids.map((id) => fetchPersonGroupMembers(id))).then(
      (results) => {
        if (cancelled) return;
        const programIds = results.flatMap((r) =>
          r.status === "fulfilled" && r.value?.program_id ? [r.value.program_id] : [],
        );
        setDirectedProgramIds(Array.from(new Set(programIds)));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [directedRotationKey]);

  // Programs the caller administers, plus — for a Rotation Director —
  // the programs of the rotations they direct. The server scopes the
  // list per program either way (a director only gets their own).
  const programs = useMemo(() => {
    const eligible = (administeredPrograms ?? []).filter(
      (p): p is NonNullable<typeof p> => !!p && !!p.id && !!p.name,
    );
    const visible = programsVisibleTo(eligible, isSysAdmin);
    const withRotations = visible.filter(
      (p) =>
        featuresByProgramId[p.id]?.rotations === true &&
        administersProgram(adminAuthority, p.id),
    );
    const administered = alphabetizePrograms(withRotations, true).map((p) => ({
      id: p.id,
      label: programLabel(p),
    }));
    const seen = new Set(administered.map((p) => p.id));
    const directed: { id: string; label: string }[] = [];
    for (const programId of directedProgramIds ?? []) {
      if (seen.has(programId)) continue;
      seen.add(programId);
      const program = (allPrograms ?? []).find((p) => p?.id === programId);
      directed.push({
        id: programId,
        label: program?.name ? programLabel(program) : programId,
      });
    }
    directed.sort((a, b) => a.label.localeCompare(b.label));
    return [...administered, ...directed];
  }, [
    administeredPrograms,
    allPrograms,
    isSysAdmin,
    featuresByProgramId,
    adminAuthority,
    directedProgramIds,
  ]);

  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  useEffect(() => {
    if (programs.length > 0 && !selectedProgramId) {
      setSelectedProgramId(programs[0].id);
    }
  }, [programs, selectedProgramId]);

  // Admins of the selected program manage everything; a Rotation
  // Director only adds / removes members of their own rotations.
  const canAdminister = administersProgram(adminAuthority, selectedProgramId);

  // --- Role catalog (T6) — Rotations manages the person_group-axis roles ---
  const catalog = useKnownRoles();
  const managedRoles = useMemo(() => {
    if (!catalog.data) return [];
    return rolesOnAxis(catalog.data, "person_group");
  }, [catalog.data]);

  // --- Rotations list for this program ---
  const rotationsList = usePersonGroups(
    selectedProgramId ? selectedProgramId : null,
  );
  // SE-2832: the networks a rotation of this program may belong to, listed
  // with the rotations by simpl_auth (the table it checks them against).
  const programNetworks = useMemo(
    () => rotationsList.data?.networks ?? [],
    [rotationsList.data],
  );

  // --- Program-wide director grants — feeds the archive confirm's
  //     "which directors will lose access" calculation, and the list
  //     row's "N directors" column.
  //     Admin-only: `role-scopes` refuses a director who isn't an admin.
  const grants = useRoleScopes(
    managedRoles,
    selectedProgramId && canAdminister ? "person_group" : null,
    (canAdminister && selectedProgramId) || null,
  );

  const grantsByRotation = useMemo(() => {
    const map = new Map<string, RoleScope[]>();
    for (const row of grants.data?.role_scopes ?? []) {
      if (!row.scope_id) continue;
      if (!map.has(row.scope_id)) map.set(row.scope_id, []);
      map.get(row.scope_id)!.push(row);
    }
    return map;
  }, [grants.data]);

  // --- Director picker population (Turso: active Attendings in program) ---
  const [directorCandidates, setDirectorCandidates] = useState<Grantee[]>([]);
  useEffect(() => {
    if (!selectedProgramId || !canAdminister) {
      setDirectorCandidates([]);
      return;
    }
    let cancelled = false;
    directorGrantees(selectedProgramId)
      .then((rows) => {
        if (!cancelled) setDirectorCandidates(rows);
      })
      .catch(() => {
        if (!cancelled) setDirectorCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProgramId, canAdminister]);

  // --- Page-level banner (create/rename/archive succeeded or failed) ---
  const [banner, setBanner] = useState<
    { severity: "success" | "error" | "info"; message: string } | null
  >(null);

  // --- Detail dialog state ---
  const [openRotationId, setOpenRotationId] = useState<string | null>(null);
  const openRotation = useMemo(() => {
    if (!openRotationId || !selectedProgramId) return null;
    const found = rotationsList.data?.groups.find(
      (g) => g.id === openRotationId,
    );
    if (!found) return null;
    // The LIST payload from simpl_auth's person_groups_payload omits
    // program_id per row (the caller already knows it — it's the query
    // param). But the detail dialog reads `rotation.program_id` to key
    // its own `useRoleScopes` fetch, so we inject the currently-selected
    // program here. Without this the dialog's directors hook short-
    // circuits to idle (roles OK, programId undefined) and the section
    // renders empty — the "designated a director but the modal doesn't
    // update" bug.
    return { ...found, program_id: selectedProgramId };
  }, [openRotationId, rotationsList.data, selectedProgramId]);

  // --- Create dialog ---
  const [createOpen, setCreateOpen] = useState(false);

  // --- Rename dialog ---
  const [renameTarget, setRenameTarget] = useState<PersonGroup | null>(null);

  // --- Archive confirm ---
  const [archiveTarget, setArchiveTarget] = useState<PersonGroup | null>(null);

  // --- Directorship save (assign / revoke) — kept at the page level so
  //     the detail dialog AND the archive confirm can share one flow.
  //     Returns a discriminated result so each caller renders its own
  //     banner (in the dialog vs on the page) rather than the flow
  //     reaching into the parent's state. Copy is end-user-safe: raw
  //     error text is logged, not surfaced.
  const [directorSaveInFlight, setDirectorSaveInFlight] = useState<
    string | null
  >(null);

  const runDirectorSave = async (input: {
    directorEmail: string;
    directorName: string;
    role: string;
    /**
     * The ONE change to make: a rotation to add (assign) or rotation ids
     * to drop (revoke). The director's other rotations are read fresh on
     * save and carried — see `nextScopeSet` (SE-2841).
     */
    change: { add?: ScopeBinding[]; remove?: string[] };
    kind: SaveKind;
    rotationName: string;
    scopeIdForBadge?: string;
  }): Promise<{ ok: true } | { ok: false; message: string }> => {
    setDirectorSaveInFlight(
      `${input.directorEmail}::${input.scopeIdForBadge ?? ""}`,
    );
    try {
      const currentAccess = await fetchUserAccess(input.directorEmail);
      // The whole outbound set for THIS role: every current ADMIN
      // directorship (all programs, as of now) with this one change. The
      // Lambda works out the flat roles from it, and ends the Rotation
      // Director label in a program whose last directorship goes (SE-3027).
      const outbound = nextScopeSet(
        currentAccess?.role_scopes ?? [],
        input.role,
        "person_group",
        input.change,
      );

      await putUserAccess({
        email: input.directorEmail,
        person_group_scopes: { [input.role]: outbound },
      });
      grants.reload();
      return { ok: true };
    } catch (err: unknown) {
      // Log the technical detail; return a plain-language message.
      // eslint-disable-next-line no-console
      console.error("runDirectorSave failed", err);
      grants.reload();
      const status =
        (err as { response?: { status?: number }; status?: number })?.response
          ?.status ??
        (err as { status?: number })?.status;
      // 409 is genuinely stale-state (a concurrent write moved things);
      // 403 / 422 carry the server's end-user reason (e.g. not an
      // Attending); anything else is "something went wrong, we've reloaded".
      const generic = t(
        "Something went wrong saving that change. The latest is shown now — please try again.",
      );
      const message =
        status === 409
          ? refusalCopy.staleWrite
          : status === 403 || status === 422
            ? apiErrorMessage(err, generic)
            : generic;
      return { ok: false, message };
    } finally {
      setDirectorSaveInFlight(null);
    }
  };

  // --- Archive flow: revoke ADMIN grants first, THEN PATCH archived=true.
  //     The ordering is load-bearing; see the file header.
  const archiveRotation = async (rotation: PersonGroup) => {
    setBanner(null);
    const rows = grantsByRotation.get(rotation.id) ?? [];
    const adminRows = rows.filter((r) => isAdminOwned(r.data_source_name));
    const foreignRows = rows.filter((r) => !isAdminOwned(r.data_source_name));

    // Revoke each ADMIN grant. Group by director so we make ONE
    // preserve-current-flat-grants save per director (not one per row).
    const rowsByEmail = new Map<string, RoleScope[]>();
    for (const r of adminRows) {
      const email = r.email.toLowerCase();
      if (!rowsByEmail.has(email)) rowsByEmail.set(email, []);
      rowsByEmail.get(email)!.push(r);
    }
    let revoked = 0;
    for (const [email, dRows] of Array.from(rowsByEmail.entries())) {
      // For each director, drop this rotation's binding(s).
      const role = dRows[0].role ?? managedRoles[0] ?? "RotationDirector";
      const displayName =
        directorCandidates.find((c) => c.email === email)?.displayName ?? email;
      const res = await runDirectorSave({
        directorEmail: email,
        directorName: displayName,
        role,
        change: { remove: [rotation.id] },
        kind: "revoke",
        rotationName: rotation.label,
        scopeIdForBadge: rotation.id,
      });
      if (!res.ok) {
        setBanner({
          severity: "error",
          message: t(
            "Archive aborted: couldn't revoke one of the current directorships. The rotation is still active; please try again.",
          ),
        });
        return;
      }
      revoked += dRows.length;
    }

    // Now archive. This is where "once archived the choices provider
    // drops it and the binding can no longer be addressed" bites — we
    // ran the revokes first for exactly this reason.
    try {
      await patchPersonGroup(rotation.id, { archived: true });
      setBanner({
        severity: "success",
        message: archiveCopy.archived(rotation.label, revoked),
      });
      rotationsList.reload();
      if (foreignRows.length > 0) {
        // Surface the disclosure that a synced directorship remains as a
        // record but confers access to nothing on this rotation.
        setBanner({
          severity: "info",
          message:
            archiveCopy.archived(rotation.label, revoked) +
            " " +
            archiveCopy.syncedDirectorshipsRemain(foreignRows.length),
        });
      }
      if (openRotationId === rotation.id) setOpenRotationId(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setBanner({
        severity: "error",
        message: t(
          "Directors were revoked, but archiving the rotation itself failed. It stays active. ({{err}})",
          { err: message },
        ),
      });
      rotationsList.reload();
      grants.reload();
    }
    setArchiveTarget(null);
  };

  // --- Unarchive: PATCH archived=false. The directorships the archive
  //     revoked stay revoked; the success copy says so.
  const unarchiveRotation = async (rotation: PersonGroup) => {
    setBanner(null);
    try {
      await patchPersonGroup(rotation.id, { archived: false });
      setBanner({
        severity: "success",
        message: archiveCopy.unarchived(rotation.label),
      });
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      setBanner({
        severity: "error",
        message:
          status === 409
            ? refusalCopy.unarchiveCollision(rotation.label)
            : apiErrorMessage(
                err,
                t("Couldn't unarchive \"{{label}}\". Please try again.", {
                  label: rotation.label,
                }),
              ),
      });
    }
    rotationsList.reload();
  };

  // --- Create ---
  const createRotation = async (
    label: string,
    networkId: string | null,
  ): Promise<boolean> => {
    if (!selectedProgramId) return false;
    setBanner(null);
    try {
      const created = await createPersonGroup({
        programId: selectedProgramId,
        label,
        memberRoles: ["Attending"],
        networkId,
      });
      setBanner({
        severity: "success",
        message: t('Rotation "{{label}}" created.', { label: created.label }),
      });
      rotationsList.reload();
      return true;
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 409) {
        setBanner({
          severity: "error",
          message: refusalCopy.activeLabelCollision(label),
        });
      } else {
        const fallback = err instanceof Error ? err.message : String(err);
        setBanner({ severity: "error", message: apiErrorMessage(err, fallback) });
      }
      return false;
    }
  };

  // --- Network (SE-2832) ---
  const changeRotationNetwork = async (
    rotation: PersonGroup,
    networkId: string | null,
  ): Promise<boolean> => {
    setBanner(null);
    try {
      await patchPersonGroup(rotation.id, { networkId });
      const network = programNetworks.find((n) => n.id === networkId);
      setBanner({
        severity: "success",
        message: network
          ? t('"{{label}}" is now in {{network}}.', {
              label: rotation.label,
              network: network.name || network.id,
            })
          : t('"{{label}}" now covers the whole program.', {
              label: rotation.label,
            }),
      });
      rotationsList.reload();
      return true;
    } catch (err: any) {
      const fallback = err instanceof Error ? err.message : String(err);
      setBanner({ severity: "error", message: apiErrorMessage(err, fallback) });
      return false;
    }
  };

  // --- Rename ---
  const renameRotation = async (
    rotation: PersonGroup,
    label: string,
  ): Promise<boolean> => {
    setBanner(null);
    try {
      await patchPersonGroup(rotation.id, { label });
      setBanner({
        severity: "success",
        message: t('Rotation renamed to "{{label}}".', { label }),
      });
      rotationsList.reload();
      return true;
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 409) {
        setBanner({
          severity: "error",
          message: refusalCopy.renameCollision(label),
        });
      } else {
        const fallback = err instanceof Error ? err.message : String(err);
        setBanner({ severity: "error", message: apiErrorMessage(err, fallback) });
      }
      return false;
    }
  };

  const programsLoading =
    programsStatus === "loading" ||
    programsStatus === "idle" ||
    featuresStatus === "loading" ||
    featuresStatus === "idle" ||
    directedProgramIds === null;
  const programsFailed = programsStatus === "failed";
  const hasPrograms = programs.length > 0;

  // Split active vs archived — the ticket says archived groups leave
  // the visible list. We fold archived rows into a bottom "Archived"
  // subsection so they're still reachable for reference (they cannot be
  // acted on).
  const activeRotations = useMemo(
    () =>
      (rotationsList.data?.groups ?? []).filter((g) => g.archived_at === null),
    [rotationsList.data],
  );
  const archivedRotations = useMemo(
    () =>
      (rotationsList.data?.groups ?? []).filter((g) => g.archived_at !== null),
    [rotationsList.data],
  );

  return (
    <PageWrapper className="Rotations" headerText="Rotations">
      <Typography variant="body1" sx={{ mb: 2 }}>
        {t(
          "Rotations are the groupings a program uses to organise its rotations of trainees. Each rotation has members (roster data — takes effect immediately) and directors (a grant of dashboard access — takes effect at next sign-in).",
        )}
      </Typography>

      {programsLoading ? (
        <Loading text={t("Loading your programs…")} />
      ) : programsFailed ? (
        <Alert severity="error" sx={{ maxWidth: 640 }}>
          <AlertTitle>{t("Failed to load your programs")}</AlertTitle>
          {programsError ? (
            <Typography
              variant="caption"
              component="pre"
              sx={{
                mt: 1,
                mb: 1,
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            >
              {programsError}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={retryLoadPrograms}
            >
              {t("Retry")}
            </Button>
          </Stack>
        </Alert>
      ) : !hasPrograms ? (
        <Alert severity="info">
          {t(
            "You don't administer or direct rotations in any program with rotations turned on, so this page has nothing to show.",
          )}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {/* Program picker */}
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <FormControl size="small" sx={{ width: 300 }}>
              <InputLabel shrink id="rotations-program-label">
                {t("Program")}
              </InputLabel>
              <Select
                labelId="rotations-program-label"
                label={t("Program")}
                displayEmpty
                value={selectedProgramId}
                input={<OutlinedInput notched label={t("Program")} />}
                onChange={(e) => setSelectedProgramId(String(e.target.value))}
                data-testid="rotations-program-picker"
              >
                {programs.map((program) => (
                  <MenuItem key={program.id} value={program.id}>
                    {program.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {canAdminister && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                disabled={!selectedProgramId}
                data-testid="rotations-new-button"
                sx={{ height: 40 }}
              >
                {t("NEW ROTATION")}
              </Button>
            )}
          </Stack>

          {selectedProgramId && !canAdminister && (
            <Alert severity="info" data-testid="rotations-director-note">
              {t(rotationAccessCopy.directorLimitedView)}
            </Alert>
          )}

          {banner && (
            <Alert
              severity={banner.severity}
              onClose={() => setBanner(null)}
              data-testid="rotations-page-banner"
            >
              {banner.message}
            </Alert>
          )}

          {/* Rotations list */}
          {rotationsList.status === "idle" && (
            <Typography variant="body2" color="text.secondary">
              {t("Choose a program to see its rotations.")}
            </Typography>
          )}
          {rotationsList.status === "loading" && (
            <Loading text={t("Loading rotations…")} />
          )}
          {rotationsList.status === "failed" && (
            <Alert severity="error">
              <AlertTitle>{t("Couldn't load rotations")}</AlertTitle>
              {rotationsList.error ? <code>{rotationsList.error}</code> : null}
            </Alert>
          )}
          {rotationsList.status === "succeeded" && (
            <>
              <RotationsTable
                title={t("Rotations")}
                rows={activeRotations}
                grantsByRotation={grantsByRotation}
                onOpen={(g) => setOpenRotationId(g.id)}
                onRename={(g) => setRenameTarget(g)}
                onArchive={(g) => setArchiveTarget(g)}
                canManage={canAdminister}
                testId="rotations-active-table"
                emptyText={
                  canAdminister
                    ? t("No rotations yet. Click NEW ROTATION above to add one.")
                    : t("You don't direct any active rotations in this program.")
                }
              />
              {archivedRotations.length > 0 && (
                <RotationsTable
                  title={t("Archived")}
                  rows={archivedRotations}
                  grantsByRotation={grantsByRotation}
                  onOpen={(g) => setOpenRotationId(g.id)}
                  onRename={() => {}}
                  onArchive={() => {}}
                  onUnarchive={unarchiveRotation}
                  canManage={canAdminister}
                  testId="rotations-archived-table"
                  archived
                  emptyText=""
                />
              )}
            </>
          )}
        </Stack>
      )}

      {/* Detail dialog */}
      {openRotation && (
        <RotationDetailDialog
          rotation={openRotation}
          managedRoles={managedRoles}
          directorCandidates={directorCandidates}
          runDirectorSave={runDirectorSave}
          onRequestRename={(r) => setRenameTarget(r)}
          onRequestArchive={(r) => setArchiveTarget(r)}
          onClose={() => setOpenRotationId(null)}
          directorSaveInFlight={directorSaveInFlight}
          canAdminister={canAdminister}
          networks={programNetworks}
          onChangeNetwork={changeRotationNetwork}
        />
      )}

      {/* Create dialog */}
      {createOpen && selectedProgramId && canAdminister && (
        <CreateRotationDialog
          onClose={() => setCreateOpen(false)}
          onCreate={createRotation}
          networks={programNetworks}
        />
      )}

      {/* Rename dialog */}
      {renameTarget && canAdminister && (
        <RenameRotationDialog
          rotation={renameTarget}
          onClose={() => setRenameTarget(null)}
          onRename={renameRotation}
        />
      )}

      {/* Archive confirm */}
      {archiveTarget && canAdminister && (
        <ArchiveConfirmDialog
          rotation={archiveTarget}
          grants={grantsByRotation.get(archiveTarget.id) ?? []}
          directorCandidates={directorCandidates}
          onClose={() => setArchiveTarget(null)}
          onConfirm={() => archiveRotation(archiveTarget)}
        />
      )}
    </PageWrapper>
  );
};

// -----------------------------------------------------------------------------
// Sub-components — kept in this file to match the internal template's
// monolithic shape. The larger `RotationDetailDialog` lives in its own
// file because it's the surface every acceptance-criteria test touches.
// -----------------------------------------------------------------------------

const RotationsTable = ({
  title,
  rows,
  grantsByRotation,
  onOpen,
  onRename,
  onArchive,
  onUnarchive,
  canManage,
  testId,
  archived = false,
  emptyText,
}: {
  title: string;
  rows: PersonGroup[];
  grantsByRotation: Map<string, RoleScope[]>;
  onOpen: (rotation: PersonGroup) => void;
  onRename: (rotation: PersonGroup) => void;
  onArchive: (rotation: PersonGroup) => void;
  /** Archived table only: restore the rotation to active. */
  onUnarchive?: (rotation: PersonGroup) => void;
  /** Program admin: directors column plus rename / archive / unarchive actions. */
  canManage: boolean;
  testId: string;
  archived?: boolean;
  emptyText: string;
}) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        emptyText ? (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        ) : null
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            boxShadow: "none",
            "& .MuiTableCell-root": { py: 1 },
            "& .MuiTableCell-head": { fontWeight: 500 },
          }}
        >
          <Table data-testid={testId}>
            <TableHead>
              <TableRow>
                <TableCell>{t("Rotation")}</TableCell>
                {canManage && (
                  <TableCell align="right">{t("Directors")}</TableCell>
                )}
                <TableCell align="right">{t("Members")}</TableCell>
                <TableCell width={120} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((rotation) => {
                const directorCount =
                  grantsByRotation.get(rotation.id)?.length ?? 0;
                return (
                  <TableRow
                    key={rotation.id}
                    hover
                    data-rotation-id={rotation.id}
                  >
                    <TableCell>
                      <Button
                        onClick={() => onOpen(rotation)}
                        sx={{
                          textTransform: "none",
                          justifyContent: "flex-start",
                          textAlign: "left",
                          minWidth: 0,
                          p: 0,
                        }}
                        data-testid="rotations-row-open"
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {rotation.label}
                        </Typography>
                      </Button>
                      {archived && (
                        <Chip
                          size="small"
                          label={t("Archived")}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">{directorCount}</TableCell>
                    )}
                    <TableCell align="right">
                      {rotation.member_count ?? 0}
                    </TableCell>
                    <TableCell align="right">
                      {!archived && canManage && (
                        <>
                          <Tooltip title={t("Rename")}>
                            <IconButton
                              size="small"
                              onClick={() => onRename(rotation)}
                              aria-label={t("Rename {{label}}", {
                                label: rotation.label,
                              })}
                              data-testid="rotations-row-rename"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("Archive")}>
                            <IconButton
                              size="small"
                              onClick={() => onArchive(rotation)}
                              aria-label={t("Archive {{label}}", {
                                label: rotation.label,
                              })}
                              data-testid="rotations-row-archive"
                            >
                              <ArchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {archived && canManage && onUnarchive && (
                        <Tooltip title={t("Unarchive")}>
                          <IconButton
                            size="small"
                            onClick={() => onUnarchive(rotation)}
                            aria-label={t("Unarchive {{label}}", {
                              label: rotation.label,
                            })}
                            data-testid="rotations-row-unarchive"
                          >
                            <UnarchiveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

const CreateRotationDialog = ({
  onClose,
  onCreate,
  networks,
}: {
  onClose: () => void;
  onCreate: (label: string, networkId: string | null) => Promise<boolean>;
  networks: ProgramNetwork[];
}) => {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [networkId, setNetworkId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSaving(true);
    const ok = await onCreate(trimmed, networkId);
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("New rotation")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            "Name a rotation for this program. Members and directors are added inside the rotation once it exists.",
          )}
        </DialogContentText>
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          data-testid="rotations-create-member-roles"
        >
          {t(rotationAccessCopy.membersAreAttendings)}
        </Alert>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={t("Rotation name")}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={saving}
          inputProps={{ "data-testid": "rotations-create-label-input" }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && label.trim()) void submit();
          }}
        />
        <Box sx={{ mt: 2 }}>
          <RotationNetworkSelect
            networks={networks}
            value={networkId}
            onChange={setNetworkId}
            disabled={saving}
            testId="rotations-create-network"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!label.trim() || saving}
          data-testid="rotations-create-submit"
        >
          {saving ? <CircularProgress size={16} /> : t("Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RenameRotationDialog = ({
  rotation,
  onClose,
  onRename,
}: {
  rotation: PersonGroup;
  onClose: () => void;
  onRename: (rotation: PersonGroup, label: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  const [label, setLabel] = useState(rotation.label);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === rotation.label) {
      onClose();
      return;
    }
    setSaving(true);
    const ok = await onRename(rotation, trimmed);
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("Rename rotation")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            "Renaming keeps the rotation's identity — existing memberships and directorships stay attached.",
          )}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={t("Rotation name")}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={saving}
          inputProps={{ "data-testid": "rotations-rename-label-input" }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && label.trim()) void submit();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!label.trim() || saving}
          data-testid="rotations-rename-submit"
        >
          {saving ? <CircularProgress size={16} /> : t("Rename")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ArchiveConfirmDialog = ({
  rotation,
  grants,
  directorCandidates,
  onClose,
  onConfirm,
}: {
  rotation: PersonGroup;
  grants: RoleScope[];
  directorCandidates: Grantee[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const admin = grants.filter((g) => isAdminOwned(g.data_source_name));
  const foreign = grants.filter((g) => !isAdminOwned(g.data_source_name));

  const displayName = (email: string) =>
    directorCandidates.find((c) => c.email.toLowerCase() === email.toLowerCase())
      ?.displayName ?? email;

  const submit = async () => {
    setSaving(true);
    try {
      await onConfirm();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle data-testid="rotations-archive-title">
        {archiveCopy.confirmHeader(rotation.label)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {admin.length === 0 ? (
            <Alert severity="info">{archiveCopy.noDirectorsLoseAccess}</Alert>
          ) : (
            <>
              <Alert severity="warning">
                <AlertTitle>
                  {archiveCopy.directorsLoseAccess(admin.length)}
                </AlertTitle>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {admin.map((row) => (
                    <Typography
                      variant="body2"
                      key={`${row.email}::${row.scope_id}`}
                    >
                      · {displayName(row.email)}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        ({row.email})
                      </Typography>
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            </>
          )}
          {foreign.length > 0 && (
            <Alert severity="info">
              {archiveCopy.syncedDirectorshipsRemain(foreign.length)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={saving}
          data-testid="rotations-archive-confirm"
        >
          {saving ? <CircularProgress size={16} /> : t("Archive")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Loading = ({ text }: { text: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CircularProgress size={16} />
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);
